import { useState } from "react";
import { styled } from "@pigment-css/react";
import * as Dialog from "@radix-ui/react-dialog";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { CalendarDays, CheckMark2Small } from "@acto/web-common/components";
import { DateRangePicker } from "@acto/taskpane/field-sales-2/components/primitives/DateRangePicker";
import { toIsoDate, utcDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { DimOverlay } from "@acto/taskpane/field-sales-2/components/primitives/DimOverlay";
import { formatNumericDateRange, formatShortMonthYearRange } from "@acto/taskpane/field-sales-2/utils/format";
import {
  PeriodPreset,
  presetDateRange,
  type PeriodSelection,
} from "@acto/taskpane/field-sales-2/utils/periodSelection";
import { MorphPill } from "./ui/MorphPill";

/** Parse a backend `YYYY-MM-DD` LocalDate into the UTC-midnight Date the calendar primitives use. */
const parseIsoDate = (value: string): Date => {
  const [year, month, day] = value.split("-").map(Number);
  return utcDate(year, month - 1, day);
};

const Option = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[12],
  width: "100%",
  padding: `${theme.fieldSalesTwo.spacing[10]} ${theme.fieldSalesTwo.spacing[12]}`,
  borderRadius: theme.fieldSalesTwo.radius[16],
  border: "none",
  backgroundColor: "transparent",
  cursor: "pointer",
  textAlign: "left",
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
  outline: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: theme.fieldSalesTwo.colors.neutral[200],
  },
  "&:focus-visible": {
    backgroundColor: theme.fieldSalesTwo.colors.neutral[200],
  },
}));

const OptionCheck = styled("span")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  color: theme.fieldSalesTwo.colors.accent.action,
}));

const OptionDivider = styled("div")(({ theme }) => ({
  height: theme.strokeWidth.thin,
  margin: `${theme.fieldSalesTwo.spacing[4]} ${theme.fieldSalesTwo.spacing[8]}`,
  background: theme.fieldSalesTwo.colors.neutral[200],
}));

// The custom-range picker opens as a full-screen modal (Figma 2172-12443).
const ModalContent = styled(Dialog.Content)(({ theme }) => ({
  position: "fixed",
  inset: 0,
  zIndex: 1101,
  display: "flex",
  background: theme.fieldSalesTwo.colors.background,
  padding: theme.fieldSalesTwo.spacing[16],
  boxSizing: "border-box",
  outline: "none",
  fontSize: theme.fieldSalesTwo.font.size.base,
}));

interface PresetOption {
  key: string;
  selection: PeriodSelection;
  label: string;
}

const isSamePreset = (a: PeriodSelection, b: PeriodSelection): boolean =>
  a.kind === "preset" && b.kind === "preset" ? a.preset === b.preset : a.kind === b.kind;

export interface PeriodPickerProps {
  selection: PeriodSelection;
  onSelectionChange: (selection: PeriodSelection) => void;
}

export function PeriodPicker({ selection, onSelectionChange }: PeriodPickerProps) {
  const tCommon = useTranslations("common");
  const tDateRange = useTranslations("dateRange");
  const { locale } = useLocale();

  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  // The pill shows the resolved date window, never the preset's name.
  const label = (() => {
    if (selection.kind === "custom") {
      return formatNumericDateRange({ start: selection.range.startDate, end: selection.range.endDate }, locale);
    }
    const range = presetDateRange(selection.preset);
    return formatShortMonthYearRange({ start: range.startDate, end: range.endDate }, locale);
  })();

  const presetOptions: PresetOption[] = [
    {
      key: "last30Days",
      selection: { kind: "preset", preset: PeriodPreset.LAST_MONTH },
      label: tDateRange("last30Days"),
    },
    {
      key: "last3Months",
      selection: { kind: "preset", preset: PeriodPreset.LAST_3_MONTHS },
      label: tDateRange("last3Months"),
    },
    {
      key: "last6Months",
      selection: { kind: "preset", preset: PeriodPreset.LAST_6_MONTHS },
      label: tDateRange("last6Months"),
    },
    {
      key: "last12Months",
      selection: { kind: "preset", preset: PeriodPreset.LAST_12_MONTHS },
      label: tDateRange("last12Months"),
    },
    {
      key: "last24Months",
      selection: { kind: "preset", preset: PeriodPreset.LAST_24_MONTHS },
      label: tDateRange("last24Months"),
    },
    {
      key: "yearToDate",
      selection: { kind: "preset", preset: PeriodPreset.YEAR_TO_DATE },
      label: tCommon("dates.yearToDate"),
    },
  ];

  const handleSelect = (next: PeriodSelection) => {
    onSelectionChange(next);
    setOpen(false);
  };

  const handleCustomOpen = () => {
    setOpen(false);
    setSheetOpen(true);
  };

  const handleRangeConfirm = (range: { start: Date; end: Date }) => {
    onSelectionChange({ kind: "custom", range: { startDate: toIsoDate(range.start), endDate: toIsoDate(range.end) } });
    setSheetOpen(false);
  };

  // Shared option list, rendered both live (interactive) and in MorphPill's hidden measurer.
  const options = (
    <>
      {presetOptions.map((opt) => (
        <Option key={opt.key} type="button" role="option" onClick={() => handleSelect(opt.selection)}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CalendarDays />
            {opt.label}
          </div>
          {isSamePreset(selection, opt.selection) && (
            <OptionCheck>
              <CheckMark2Small width={16} height={16} />
            </OptionCheck>
          )}
        </Option>
      ))}
      <OptionDivider />
      <Option type="button" role="option" onClick={handleCustomOpen}>
        {tCommon("custom")}
        {selection.kind === "custom" && (
          <OptionCheck>
            <CheckMark2Small width={16} height={16} />
          </OptionCheck>
        )}
      </Option>
    </>
  );

  return (
    <>
      <MorphPill open={open} onOpenChange={setOpen} label={label} ariaLabel={tCommon("periodLabel")}>
        {options}
      </MorphPill>

      <Dialog.Root open={sheetOpen} onOpenChange={setSheetOpen}>
        <Dialog.Portal>
          <DimOverlay />
          <ModalContent
            aria-describedby={undefined}
            aria-label={tCommon("custom")}
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <DateRangePicker
              value={
                selection.kind === "custom"
                  ? { start: parseIsoDate(selection.range.startDate), end: parseIsoDate(selection.range.endDate) }
                  : null
              }
              onChange={handleRangeConfirm}
              onClose={() => setSheetOpen(false)}
              includeWeekends
            />
          </ModalContent>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
